/**
 * `checkbox` — `CheckboxValue` (`specs/M2-tier1-column-types.md` §5.12, `[09§A.7 checkbox]`).
 *
 * Read gives a **real JSON boolean** while the documented write form uses the **string**
 * `"true"`: `{"checkbox": {"checked": "true"}}` `[cvs§2 checkbox]`. `parseValue` accepts both
 * and `toMondayValue` emits the boolean monday returns (recorded: `{"checked":true}` -> `"v"`,
 * `{"checked":false}` -> `""`).
 *
 * `checked: false` is stored explicitly on 189 of 245 sampled cells, so **"never checked" (no
 * row) and "unchecked" (a row holding false) are distinguishable**. `Delete` clears the cell
 * (removes the row); clicking the box writes `false` (keeps a row). Both are supported.
 * There is **no `change_simple_column_value`** for this type.
 */
import { z } from 'zod';
import { defaultSummarySql, sqlFragment } from '../sql.js';
import { toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const checkboxSettingsSchema = z.looseObject({
    color: z.string().optional(),
    hide_footer: z.boolean().optional(),
});
export const checkboxValueSchema = z.looseObject({
    checked: z.boolean(),
    changed_at: z.string().optional(),
});
import { checkboxFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
/** monday's `text` for a checked box on this account; open question Q64 asks about other locales. */
export const CHECKBOX_CHECKED_TEXT = 'v';
function readChecked(raw) {
    if (typeof raw === 'boolean')
        return raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim().toLowerCase();
        if (trimmed === 'true' || trimmed === '1' || trimmed === 'v')
            return true;
        if (trimmed === 'false' || trimmed === '0' || trimmed === '')
            return false;
    }
    if (typeof raw === 'number')
        return raw !== 0;
    throw new ColumnValueError('checkbox', `"${String(raw)}" is not a boolean`);
}
function coerce(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'boolean')
        return { checked: raw };
    if (typeof raw === 'object') {
        const record = raw;
        if (record.checked === undefined || record.checked === null)
            return null;
        const value = { checked: readChecked(record.checked) };
        if (keepVolatile && typeof record.changed_at === 'string')
            value.changed_at = record.changed_at;
        return value;
    }
    return { checked: readChecked(raw) };
}
export const checkboxColumn = defineColumnType({
    type: 'checkbox',
    title: 'Checkbox',
    description: 'A single tick box; unchecked is stored, so it differs from empty.',
    apiFragmentName: 'CheckboxValue',
    legacyTypeName: 'boolean',
    /** monday's own id for a checkbox column is `check`, not `checkbox` `[cvs§2 checkbox]`. */
    idPrefix: 'check',
    valueSchema: checkboxValueSchema,
    settingsSchema: checkboxSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: checkboxSettingsSchema,
    writable: true,
    /** *"No `change_simple_column_value`"* `[09§A.7 checkbox]`. */
    supportsSimpleValue: false,
    clearForms: ['null'],
    /** monday filters checked as `is_not_empty` and unchecked as `is_empty` `[09§A.7]`. */
    filterConditions: checkboxFilterConditions,
    filterOperators: operatorsOf(checkboxFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    emptyText: '',
    parseValue(raw) {
        return coerce(raw, false);
    },
    toText(value) {
        return value?.checked === true ? CHECKBOX_CHECKED_TEXT : '';
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        return {
            text: value.checked ? CHECKBOX_CHECKED_TEXT : null,
            number: value.checked ? 1 : 0,
            date: null,
        };
    },
    isEqual(a, b) {
        // Setting `false` on an empty cell **is** a change: it creates a row.
        return (a?.checked ?? null) === (b?.checked ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { checked: value.checked };
    },
    fromMondayValue(raw) {
        return coerce(raw, true);
    },
    toMondayValue(value) {
        // monday returns a real boolean even though the documented write form is the string "true".
        return value === null ? null : { ...value, checked: value.checked };
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return checkboxSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return { checked: value.checked, updated_at: toFragmentTimestamp(value.changed_at) };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        if (!value)
            return [];
        return [{ key: value.checked ? 'checked' : 'unchecked', label: value.checked ? 'Checked' : 'Unchecked' }];
    },
    /** Two buckets, always, in monday's order (`Checked` first is *not* what it renders). */
    bucketVocabulary() {
        return [
            { key: 'unchecked', label: 'Unchecked' },
            { key: 'checked', label: 'Checked' },
        ];
    },
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                // We store `checked:false` explicitly, so "unchecked" must match `is_empty` too
                // (M1-B5 §5.3, M2 acceptance B6).
                return sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value->>'checked' <> 'true')`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.value->>'checked' = 'true'`);
            default:
                return null;
        }
    },
    summarySql(ctx) {
        if (ctx.aggregation === 'count_filled') {
            const cell = ctx.refs?.cell ?? 'cv';
            // Rendered as "checked / total" in the footer (M2 §5.12).
            const fragment = {
                text: `count(*) FILTER (WHERE ${cell}.value->>'checked' = 'true')`,
                params: [],
                shape: 'scalar',
            };
            return fragment;
        }
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map